import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaUserCheck, FaSearch, FaCreditCard, FaFileAlt, FaHistory,
  FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaSpinner,
  FaEye, FaDownload, FaPlus, FaChartLine, FaShieldAlt, FaBuilding,
  FaIdCard, FaFlag, FaGlobe, FaExternalLinkAlt
} from 'react-icons/fa';
import { creditBureauService } from '../../services/creditBureauService';
import { supabase } from '../../lib/supabase';

// 700Credit Product Definitions with required fields
const PRODUCTS = {
  CREDIT: {
    name: 'Credit Report',
    description: 'Full credit report (hard pull)',
    icon: FaCreditCard,
    color: 'red',
    required: ['firstName', 'lastName', 'ssn', 'address', 'city', 'state', 'zip'],
    showBureau: true,
    showDOB: true,
    impactsScore: true
  },
  PREQUALIFY: {
    name: 'PreQualify (Soft Pull)',
    description: 'Soft inquiry - no impact on credit score',
    icon: FaSearch,
    color: 'green',
    required: ['firstName', 'lastName', 'address', 'city', 'state', 'zip'],
    showBureau: true,
    showDOB: true,
    impactsScore: false
  },
  PRESCREEN: {
    name: 'PreScreen',
    description: 'Marketing prequalification',
    icon: FaUserCheck,
    color: 'blue',
    required: ['firstName', 'lastName', 'address', 'city', 'state', 'zip'],
    showBureau: true,
    showDOB: true,
    impactsScore: false
  },
  IDCHECK: {
    name: 'ID Check',
    description: 'Identity verification using SSN only',
    icon: FaIdCard,
    color: 'purple',
    required: ['ssn'],
    showBureau: false,
    showDOB: true,
    impactsScore: false
  },
  OFAC: {
    name: 'OFAC Check',
    description: 'Office of Foreign Assets Control screening',
    icon: FaGlobe,
    color: 'orange',
    required: ['firstName', 'lastName'],
    showBureau: false,
    showDOB: true,
    impactsScore: false
  },
  OFACB: {
    name: 'OFAC Business',
    description: 'Business OFAC screening',
    icon: FaBuilding,
    color: 'orange',
    required: ['businessName'],
    showBureau: false,
    showDOB: false,
    impactsScore: false
  },
  REDFLAG: {
    name: 'Red Flag',
    description: 'Identity fraud detection (SSN optional)',
    icon: FaFlag,
    color: 'yellow',
    required: ['firstName', 'lastName', 'address', 'city', 'state', 'zip'],
    showBureau: false,
    showDOB: true,
    impactsScore: false
  }
};

const CreditCheckManager = () => {
  const [activeTab, setActiveTab] = useState('new');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [creditReports, setCreditReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showIframe, setShowIframe] = useState(false);

  const [applicantForm, setApplicantForm] = useState({
    firstName: '',
    lastName: '',
    ssn: '',
    dob: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    email: '',
    phone: '',
    mobile: '',
    employer: '',
    position: '',
    employmentYears: '',
    monthlyIncome: '',
    housingType: 'Rent',
    housingPayment: '',
    hasCoApplicant: false,
    coFirstName: '',
    coLastName: '',
    coSsn: '',
    coDob: '',
    businessName: ''
  });

  const [pullOptions, setPullOptions] = useState({
    product: 'PREQUALIFY',
    bureau: 'TU',
    bureaus: ['TU'],
    bypassDuplicate: false
  });

  useEffect(() => {
    loadRecentReports();
  }, []);

  const loadRecentReports = async () => {
    try {
      const { data, error } = await supabase
        .from('credit_reports')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setCreditReports(data || []);
    } catch (err) {
      console.error('Error loading credit reports:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setApplicantForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const formatSSN = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 5) return `${cleaned.slice(0,3)}-${cleaned.slice(3)}`;
    return `${cleaned.slice(0,3)}-${cleaned.slice(3,5)}-${cleaned.slice(5,9)}`;
  };

  const handleSSNChange = (e) => {
    const formatted = formatSSN(e.target.value);
    setApplicantForm(prev => ({ ...prev, ssn: formatted }));
  };

  const handleCoSSNChange = (e) => {
    const formatted = formatSSN(e.target.value);
    setApplicantForm(prev => ({ ...prev, coSsn: formatted }));
  };

  const validateForm = () => {
    const productConfig = PRODUCTS[pullOptions.product];
    const required = productConfig.required;

    for (const field of required) {
      if (!applicantForm[field]?.trim()) {
        const fieldName = field.replace(/([A-Z])/g, ' $1').trim();
        setMessage({ type: 'error', text: `${fieldName} is required for ${productConfig.name}` });
        return false;
      }
    }

    // Validate SSN format if provided
    if (applicantForm.ssn && applicantForm.ssn.replace(/-/g, '').length !== 9) {
      setMessage({ type: 'error', text: 'SSN must be 9 digits' });
      return false;
    }

    // Validate co-applicant fields if enabled
    if (applicantForm.hasCoApplicant && pullOptions.product === 'CREDIT') {
      if (!applicantForm.coFirstName?.trim()) {
        setMessage({ type: 'error', text: 'Co-Applicant First Name is required' });
        return false;
      }
      if (!applicantForm.coLastName?.trim()) {
        setMessage({ type: 'error', text: 'Co-Applicant Last Name is required' });
        return false;
      }
      if (!applicantForm.coSsn?.trim()) {
        setMessage({ type: 'error', text: 'Co-Applicant SSN is required' });
        return false;
      }
      if (applicantForm.coSsn.replace(/-/g, '').length !== 9) {
        setMessage({ type: 'error', text: 'Co-Applicant SSN must be 9 digits' });
        return false;
      }
    }

    return true;
  };

  const handlePullCredit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const product = pullOptions.product;
      let result;

      const applicant = {
        firstName: applicantForm.firstName,
        lastName: applicantForm.lastName,
        ssn: applicantForm.ssn,
        dob: applicantForm.dob,
        address: applicantForm.address,
        city: applicantForm.city,
        state: applicantForm.state,
        zip: applicantForm.zip,
        email: applicantForm.email,
        phone: applicantForm.phone,
        mobile: applicantForm.mobile,
        employer: applicantForm.employer,
        position: applicantForm.position,
        employmentYears: applicantForm.employmentYears,
        monthlyIncome: applicantForm.monthlyIncome,
        housingType: applicantForm.housingType,
        housingPayment: applicantForm.housingPayment
      };

      if (applicantForm.hasCoApplicant) {
        applicant.coApplicant = {
          firstName: applicantForm.coFirstName,
          lastName: applicantForm.coLastName,
          ssn: applicantForm.coSsn,
          dob: applicantForm.coDob
        };
      }

      // Call the appropriate service method based on product
      switch (product) {
        case 'CREDIT':
          result = await creditBureauService.hardPull(applicant, {
            bureaus: pullOptions.bureaus,
            bypassDuplicate: pullOptions.bypassDuplicate
          });
          break;
        case 'PREQUALIFY':
          result = await creditBureauService.softPull(applicant, {
            bureau: pullOptions.bureau
          });
          break;
        case 'PRESCREEN':
          result = await creditBureauService.prescreen(applicant);
          break;
        case 'IDCHECK':
          result = await creditBureauService.idCheck(applicant);
          break;
        case 'OFAC':
          result = await creditBureauService.ofacCheck(applicant);
          break;
        case 'OFACB':
          result = await creditBureauService.ofacBusinessCheck(applicantForm.businessName);
          break;
        case 'REDFLAG':
          result = await creditBureauService.redFlagCheck(applicant);
          break;
        default:
          throw new Error(`Unknown product: ${product}`);
      }

      const productConfig = PRODUCTS[product];
      setMessage({ 
        type: 'success', 
        text: `${productConfig.name} completed successfully!${result.score ? ` Score: ${result.score}` : ''}` 
      });
      
      setSelectedReport(result);
      setShowReportModal(true);
      loadRecentReports();

    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  };

  const viewReport = (report) => {
    setSelectedReport(report);
    setShowReportModal(true);
  };

  const getScoreColor = (score) => {
    if (!score) return 'text-gray-500';
    if (score >= 720) return 'text-green-500';
    if (score >= 680) return 'text-green-400';
    if (score >= 640) return 'text-yellow-500';
    if (score >= 600) return 'text-orange-500';
    return 'text-red-500';
  };

  const getTierBadge = (decision) => {
    if (!decision) return null;
    
    const tierColors = {
      'TIER 1': 'bg-green-500',
      'TIER 2': 'bg-green-400',
      'TIER 3': 'bg-yellow-500',
      'TIER 4': 'bg-orange-500',
      'TIER 5': 'bg-red-400',
      'TIER 6': 'bg-red-600'
    };

    const tier = Object.keys(tierColors).find(t => decision.includes(t));
    return tier ? (
      <span className={`${tierColors[tier]} text-white text-xs px-2 py-1 rounded`}>
        {tier}
      </span>
    ) : null;
  };

  const resetForm = () => {
    setApplicantForm({
      firstName: '',
      lastName: '',
      ssn: '',
      dob: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      email: '',
      phone: '',
      mobile: '',
      employer: '',
      position: '',
      employmentYears: '',
      monthlyIncome: '',
      housingType: 'Rent',
      housingPayment: '',
      hasCoApplicant: false,
      coFirstName: '',
      coLastName: '',
      coSsn: '',
      coDob: '',
      businessName: ''
    });
    setMessage({ type: '', text: '' });
  };

  const currentProduct = PRODUCTS[pullOptions.product];
  const isFieldRequired = (fieldName) => currentProduct.required.includes(fieldName);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <FaCreditCard className="text-primary-500" />
          Credit Bureau Manager
        </h2>
        <p className="text-gray-600 mt-1">Pull soft and hard credit reports via 700Credit</p>
      </div>

      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mb-4 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
          }`}
        >
          {message.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
          {message.text}
        </motion.div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        {[
          { id: 'new', label: 'New Credit Pull', icon: FaPlus },
          { id: 'history', label: 'Pull History', icon: FaHistory }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === tab.id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <tab.icon />
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'new' && (
        <form onSubmit={handlePullCredit} className="space-y-6">
          {/* Product Selection */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FaShieldAlt className="text-primary-500" />
              Select Product
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(PRODUCTS).map(([key, product]) => {
                const Icon = product.icon;
                const colorClasses = {
                  red: 'border-red-500 bg-red-500/10',
                  green: 'border-green-500 bg-green-500/10',
                  blue: 'border-blue-500 bg-blue-500/10',
                  purple: 'border-purple-500 bg-purple-500/10',
                  orange: 'border-orange-500 bg-orange-500/10',
                  yellow: 'border-yellow-500 bg-yellow-500/10'
                };
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPullOptions(prev => ({ ...prev, product: key }))}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      pullOptions.product === key
                        ? colorClasses[product.color]
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`text-${product.color}-500`} />
                      <span className="font-semibold text-gray-900 text-sm">{product.name}</span>
                    </div>
                    <div className="text-xs text-gray-600">{product.description}</div>
                    {product.impactsScore && (
                      <div className="text-xs text-red-400 mt-1">⚠️ Impacts credit score</div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Bureau Selection - only show for products that need it */}
            {currentProduct.showBureau && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Credit Bureau(s)</label>
                {pullOptions.product === 'CREDIT' ? (
                  <div className="flex flex-wrap gap-3">
                    {['TU', 'EFX', 'XPN'].map(bureau => (
                      <label key={bureau} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pullOptions.bureaus.includes(bureau)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setPullOptions(prev => ({ ...prev, bureaus: [...prev.bureaus, bureau] }));
                            } else {
                              setPullOptions(prev => ({ 
                                ...prev, 
                                bureaus: prev.bureaus.filter(b => b !== bureau) 
                              }));
                            }
                          }}
                          className="rounded text-primary-500"
                        />
                        <span className="text-gray-700">
                          {bureau === 'TU' ? 'TransUnion' : bureau === 'EFX' ? 'Equifax' : 'Experian'}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <select
                    value={pullOptions.bureau}
                    onChange={(e) => setPullOptions(prev => ({ ...prev, bureau: e.target.value }))}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                  >
                    <option value="TU">TransUnion</option>
                    <option value="EFX">Equifax</option>
                    <option value="XPN">Experian</option>
                  </select>
                )}
              </div>
            )}

            {/* Bypass Duplicate Check - only for CREDIT */}
            {pullOptions.product === 'CREDIT' && (
              <div className="mt-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pullOptions.bypassDuplicate}
                    onChange={(e) => setPullOptions(prev => ({ ...prev, bypassDuplicate: e.target.checked }))}
                    className="rounded text-primary-500"
                  />
                  <span className="text-gray-700 text-sm">Bypass duplicate check (APP_MODIFIED)</span>
                </label>
              </div>
            )}
          </div>

          {/* Business Name - only for OFACB */}
          {pullOptions.product === 'OFACB' && (
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FaBuilding className="text-primary-500" />
                Business Information
              </h3>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Name *</label>
                <input
                  type="text"
                  name="businessName"
                  value={applicantForm.businessName}
                  onChange={handleInputChange}
                  placeholder="Enter business name for OFAC screening"
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                  required
                />
              </div>
            </div>
          )}

          {/* Applicant Information - hide for OFACB */}
          {pullOptions.product !== 'OFACB' && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FaUserCheck className="text-primary-500" />
              {pullOptions.product === 'IDCHECK' ? 'Identity Information' : 'Applicant Information'}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Name fields - not needed for IDCHECK */}
              {pullOptions.product !== 'IDCHECK' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name {isFieldRequired('firstName') ? '*' : ''}
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={applicantForm.firstName}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name {isFieldRequired('lastName') ? '*' : ''}
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={applicantForm.lastName}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SSN {isFieldRequired('ssn') ? '*' : '(Optional)'}
                </label>
                <input
                  type="text"
                  name="ssn"
                  value={applicantForm.ssn}
                  onChange={handleSSNChange}
                  placeholder="XXX-XX-XXXX"
                  maxLength={11}
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                />
              </div>
              {currentProduct.showDOB && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth {pullOptions.product === 'CREDIT' ? '*' : ''}
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={applicantForm.dob}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                  />
                </div>
              )}
              {pullOptions.product !== 'IDCHECK' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={applicantForm.email}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={applicantForm.phone}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Address */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                <input
                  type="text"
                  name="address"
                  value={applicantForm.address}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                <input
                  type="text"
                  name="city"
                  value={applicantForm.city}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                  <input
                    type="text"
                    name="state"
                    value={applicantForm.state}
                    onChange={handleInputChange}
                    maxLength={2}
                    placeholder="TX"
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900 uppercase"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ZIP *</label>
                  <input
                    type="text"
                    name="zip"
                    value={applicantForm.zip}
                    onChange={handleInputChange}
                    maxLength={5}
                    className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Housing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Housing Type</label>
                <select
                  name="housingType"
                  value={applicantForm.housingType}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                >
                  <option value="Rent">Rent</option>
                  <option value="Own">Own (with mortgage)</option>
                  <option value="OwnFreeAndClear">Own (free & clear)</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Housing Payment</label>
                <input
                  type="number"
                  name="housingPayment"
                  value={applicantForm.housingPayment}
                  onChange={handleInputChange}
                  placeholder="1500"
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                />
              </div>
            </div>
          </div>
          )}

          {/* Employment Information - only for credit products */}
          {['CREDIT', 'PREQUALIFY', 'PRESCREEN'].includes(pullOptions.product) && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <FaChartLine className="text-primary-500" />
              Employment Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Employer</label>
                <input
                  type="text"
                  name="employer"
                  value={applicantForm.employer}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                <input
                  type="text"
                  name="position"
                  value={applicantForm.position}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Years Employed</label>
                <input
                  type="number"
                  name="employmentYears"
                  value={applicantForm.employmentYears}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Income</label>
                <input
                  type="number"
                  name="monthlyIncome"
                  value={applicantForm.monthlyIncome}
                  onChange={handleInputChange}
                  placeholder="5000"
                  className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                />
              </div>
            </div>
          </div>
          )}

          {/* Co-Applicant - only for CREDIT */}
          {pullOptions.product === 'CREDIT' && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="hasCoApplicant"
                checked={applicantForm.hasCoApplicant}
                onChange={handleInputChange}
                className="rounded text-primary-500 w-5 h-5"
              />
              <span className="text-lg font-semibold text-gray-900">Add Co-Applicant</span>
            </label>

            <AnimatePresence>
              {applicantForm.hasCoApplicant && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="coFirstName"
                      value={applicantForm.coFirstName}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="coLastName"
                      value={applicantForm.coLastName}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SSN <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="coSsn"
                      value={applicantForm.coSsn}
                      onChange={handleCoSSNChange}
                      placeholder="XXX-XX-XXXX"
                      maxLength={11}
                      required
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      name="coDob"
                      value={applicantForm.coDob}
                      onChange={handleInputChange}
                      className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 text-gray-900"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg font-semibold transition-colors ${
                currentProduct.impactsScore
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              } disabled:opacity-50`}
            >
              {loading ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <FaSearch />
                  Run {currentProduct.name}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              Reset
            </button>
          </div>
        </form>
      )}

      {activeTab === 'history' && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Applicant</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Type</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Bureau</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Score</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Decision</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {creditReports.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      No credit reports found
                    </td>
                  </tr>
                ) : (
                  creditReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(report.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {report.applicant_name}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span className={`px-2 py-1 rounded text-xs ${
                          report.pull_type === 'soft' 
                            ? 'bg-green-500/20 text-green-400' 
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {report.pull_type?.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {report.bureau}
                      </td>
                      <td className={`px-4 py-3 text-sm font-bold ${getScoreColor(report.score)}`}>
                        {report.score || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getTierBadge(report.decision)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <button
                          onClick={() => viewReport(report)}
                          className="text-primary-400 hover:text-primary-300 flex items-center gap-1"
                        >
                          <FaEye /> View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Report Modal */}
      <AnimatePresence>
        {showReportModal && selectedReport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowReportModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
                <h3 className="text-xl font-bold text-gray-900">Credit Report Details</h3>
                <button
                  onClick={() => setShowReportModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FaTimesCircle size={24} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Score Summary */}
                <div className="bg-gray-100 rounded-lg p-6 text-center">
                  <div className="text-gray-600 mb-2">Credit Score</div>
                  <div className={`text-6xl font-bold ${getScoreColor(selectedReport.score)}`}>
                    {selectedReport.score || 'N/A'}
                  </div>
                  <div className="mt-2">{getTierBadge(selectedReport.decision)}</div>
                  <div className="text-gray-600 mt-2">{selectedReport.decision}</div>
                </div>

                {/* Report Details */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-100 rounded-lg p-4">
                    <div className="text-gray-500 text-sm">Transaction ID</div>
                    <div className="text-gray-900 font-mono">{selectedReport.trans_id || selectedReport.transId}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4">
                    <div className="text-gray-500 text-sm">Pull Type</div>
                    <div className="text-gray-900 capitalize">{selectedReport.pull_type || selectedReport.pullType}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4">
                    <div className="text-gray-500 text-sm">Bureau</div>
                    <div className="text-gray-900">{selectedReport.bureau}</div>
                  </div>
                  <div className="bg-gray-100 rounded-lg p-4">
                    <div className="text-gray-500 text-sm">Result</div>
                    <div className="text-gray-900">{selectedReport.result_description || selectedReport.resultDescription}</div>
                  </div>
                </div>

                {/* Score Factors */}
                {(selectedReport.score_factors || selectedReport.scoreFactors)?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Score Factors</h4>
                    <ul className="space-y-2">
                      {(selectedReport.score_factors || selectedReport.scoreFactors).map((factor, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <FaExclamationTriangle className="text-yellow-500 mt-1 flex-shrink-0" />
                          {factor}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Recommendations */}
                {(selectedReport.recommendations)?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Recommendations</h4>
                    <ul className="space-y-2">
                      {selectedReport.recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-700">
                          <FaCheckCircle className="text-green-500 mt-1 flex-shrink-0" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* iFrame URL - View Full Report */}
                {(selectedReport.iframe_url || selectedReport.iframeUrl) && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-lg font-semibold text-gray-900">Full Credit Report</h4>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowIframe(!showIframe)}
                          className="flex items-center gap-2 px-3 py-1 bg-primary-600 text-white rounded hover:bg-primary-700 text-sm"
                        >
                          <FaEye /> {showIframe ? 'Hide' : 'View'} Report
                        </button>
                        <a
                          href={selectedReport.iframe_url || selectedReport.iframeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700 text-sm"
                        >
                          <FaExternalLinkAlt /> Open in New Tab
                        </a>
                      </div>
                    </div>
                    
                    {/* Embedded iFrame */}
                    {showIframe && (
                      <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
                        <iframe
                          src={selectedReport.iframe_url || selectedReport.iframeUrl}
                          width="100%"
                          height="600"
                          frameBorder="0"
                          scrolling="auto"
                          title="700Credit Report"
                          className="bg-white"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CreditCheckManager;
